import React from "react";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import {
	AboutUsHeading,
	AboutUsParagraph,
	AboutUsSection,
} from "@insightfulscience/atomic-react/r1/molecules/AboutUs";
import WistiaVideo from "@insightfulscience/atomic-react/r2/WistiaVideo";
import WistiaVideoEmbed from "@insightfulscience/atomic-react/r2/WistiaVideo/Embed";
import Line from "@insightfulscience/atomic-react/Line";
import BlockQuote, {
	BlockQuoteItem,
} from "@insightfulscience/atomic-react/r1/molecules/BlockQuote";
import Html from "@insightfulscience/smart-react/Html";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SectionTryForFree from "@src/SectionTryForFree";
import SiteLayout from "@src/SiteLayout";
import resources from "../locales/about-page";

const About = () => {
	const [t] = useLocale("about-page");
	return (
		<SiteLayout hero>
			<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={t("META_URL")}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
					isImageBaseUrl
				/>
				<PageMeta.Twitter
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					site={t("META_TWITTER_SITE")}
					card={t("META_TWITTER_CARD")}
					image={t("META_TWITTER_IMAGE")}
					creator={t("META_TWITTER_CREATOR")}
					isImageBaseUrl
				/>
				<PageMeta.Canonical href={t("META_CANONICAL")} />
				<PageMeta.Author content={t("META_AUTHOR")} />
			</PageMeta>
			<Hero>
				<HeroTitle>{t("HERO_TITLE")}</HeroTitle>
			</Hero>
			<AboutUsSection>
				<AboutUsHeading>{t("SCIENTISTS_TITLE")}</AboutUsHeading>
				<AboutUsParagraph className="my-10">{t("SCIENTISTS_PARAGRAPH")}</AboutUsParagraph>
				<WistiaVideo>
					<WistiaVideoEmbed id="m2ov5eld0q" />
				</WistiaVideo>
			</AboutUsSection>
			<Line utility={{ border: { bottom: { width: 1, color: "black--15" } } }} />
			<AboutUsSection>
				<AboutUsHeading>{t("OUR_STORY_TITLE")}</AboutUsHeading>
				<AboutUsParagraph>{t("OUR_STORY_PARAGRAPH_1")}</AboutUsParagraph>
				<AboutUsParagraph>{t("OUR_STORY_PARAGRAPH_2")}</AboutUsParagraph>
				<AboutUsParagraph>
					<Html Tag="span">
						{t("OUR_STORY_PARAGRAPH_3", { sbirLink: "https://sbir.nih.gov/" })}
					</Html>
				</AboutUsParagraph>
				<AboutUsParagraph>
					<Html Tag="span">
						{t("OUR_STORY_PARAGRAPH_4", {
							dotmaticsLink: "https://www.dotmatics.com/?utm_source=snapgene&utm_medium=referral",
						})}
					</Html>
				</AboutUsParagraph>
			</AboutUsSection>
			<AboutUsSection>
				<AboutUsHeading>{t("GUIDING_PRINCIPLES_TITLE")}</AboutUsHeading>
				<AboutUsParagraph>
					<Html Tag="span">
						{t("GUIDING_PRINCIPLES_PARAGRAPH_1", {
							wikiLink: "https://en.wikipedia.org/wiki/Human-computer_interaction",
						})}
					</Html>
				</AboutUsParagraph>
				<AboutUsParagraph>
					<Html Tag="span">
						{t("GUIDING_PRINCIPLES_PARAGRAPH_2", { multiLicenseLink: "/pricing" })}
					</Html>
				</AboutUsParagraph>
				<AboutUsParagraph>
					<Html Tag="span">
						{t("GUIDING_PRINCIPLES_PARAGRAPH_3", {
							fileFormatsLink: "/features/convert-file-formats",
						})}
					</Html>
				</AboutUsParagraph>
			</AboutUsSection>
			<AboutUsSection sm={12} md={12} lg={12}>
				<BlockQuote>
					<BlockQuote.Head>
						<BlockQuoteItem
							active
							author={t("BLOCK_QUOTE_AUTHOR")}
							institution={t("BLOCK_QUOTE_INSTITUTION")}
						>
							{t("BLOCK_QUOTE_TEXT")}
						</BlockQuoteItem>
					</BlockQuote.Head>
				</BlockQuote>
			</AboutUsSection>
			<SectionTryForFree />
		</SiteLayout>
	);
};

export default pipe(About, WithLocale({ resources }));
